import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import IconButton from "@mui/material/IconButton";
import { useParams } from "react-router-dom";
import { useSignedInUserState } from "../../hooks/useAuthState";
import { refundService } from "../../services/firebase/refundService";
import { OrderHistoryService } from "../../services/order/orderHistoryService";
import AsyncString from "../../shared/asyncString";
import AsyncCardMedia from "../../shared/asyncCardMedia";
import { CartType, CartItem } from "../../models/CartType";
import { RefundType } from "../../models/refundType";
import { viewHightMinusHeader } from "../../header";
import { formatCurrency } from "../refundOrderInStore/refundOrderInStore";

const PENDING_REFUND_STATES = [
  "PENDING_RESTAURANT",
];

const PROCESSED_REFUND_STATES = [
  "FULL_REFUND_COMPLETE",
  "FULL_REFUND_PROCESSING",
  "PARTIAL_REFUND_COMPLETE",
  "PARTIAL_REFUND_PROCESSING",
  "REFUND_REJECTED",
]

export const ViewCustomerRefundPage: React.FC = () => {
  const { refundId } = useParams<{ refundId: string }>();
  const [refund, setRefund] = useState<RefundType | null>(null);
  const [order, setOrder] = useState<CartType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [previewImage, setPreviewImage] = useState<string>("");
  const { user } = useSignedInUserState();

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !user.uid) {
        setError("You must be signed in to view your refund.");
        setLoading(false);
        return;
      }
      setError("");

      try {
        setLoading(true);
        const fetchedRefund = await refundService.fetchRefundById(refundId ?? "");
        const fetchedOrder = await OrderHistoryService.getOrderById(
          fetchedRefund.orderId
        );
        setRefund(fetchedRefund);
        setOrder(fetchedOrder);
      } catch (err) {
        setError("Unable to load refund details. Please try again later.");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [refundId, user]);

  if (loading) {
    return (
      <Box sx={{ width: "100%", height: viewHightMinusHeader }} className="patlite">
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" align="center" sx={{ mt: 4 }}>
        {error}
      </Typography>
    );
  }

  if (!refund || !order) {
    return null;
  }

  // Determine label based on order status
  const label = PENDING_REFUND_STATES.includes(order.status)
    ? "Amount requested"
    : "Amount refunded";
  const amountValue = PENDING_REFUND_STATES.includes(order.status)
    ? order.totalAmount
    : refund.refundAmount || 0;

  return (
    <Box sx={{ width: "100%" }} className="patlite">
      <Box sx={{ p: 2, maxWidth: 800, mx: "auto" }} className="patlite">
        <Typography variant="h4" align="center" gutterBottom>
          View Refund for Order{' '}
          <Typography component="span" sx={{ fontSize: '0.9em' }}>
            {refund.orderId.replace(/(.{4})/g, '$1 ').trim()}
          </Typography>
        </Typography>

        {/* Refund Summary */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Refund Status
          </Typography>
          <Chip
            label={order.status.replace(/_/g, ' ')}
            sx={{ mb: 1, textTransform: "capitalize" }}
          />

          <Typography variant="subtitle1" gutterBottom>
            Refund Request Reason
          </Typography>
          <Typography variant="body1" paragraph>
            {refund.refundMessage}
          </Typography>

          {refund.refundDecision && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>
                Response from Restaurant
              </Typography>
              <Typography variant="body1">
                {refund.refundDecision}
              </Typography>
            </>
          )}
        </Paper>

        {/* Order Details */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Order Items
          </Typography>
          <List>
            {order.items.map((item: CartItem, idx) => (
              <ListItem key={idx}>
                <ListItemAvatar>
                  <Avatar>{item.quantity}×</Avatar>
                </ListItemAvatar>
                <img
                  style={{ width: "80px", height: "80px", marginRight: 2 }}
                  src={item.menuCartItem.baseItemId}
                  alt={"Item Thumbnail"}
                />
                <ListItemText
                  primary={item.menuCartItem.name}
                  secondary={formatCurrency(item.menuCartItem.price)}
                />
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant="body2">Total Paid:</Typography>
            <Typography variant="body2">{formatCurrency(order.totalAmount)}</Typography>
          </Box>
          <Box sx={{ mt: 1 }}>
            <Typography variant="h6">
              {label}: {formatCurrency(amountValue)}
            </Typography>
          </Box>
        </Paper>

        {/* Attachments */}
        {refund.refundImages?.length > 0 && (
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Attachments
            </Typography>
            <Grid container spacing={2}>
              {refund.refundImages.map((url, i) => (
                <Grid item key={i}>
                  <Paper variant="outlined" sx={{ p: 1 }}>
                    <img
                      src={url}
                      alt={`Attachment ${i + 1}`}
                      style={{ width: 100, height: 100, objectFit: 'cover', cursor: 'pointer' }}
                      onClick={() => setPreviewImage(url)}
                    />
                    <IconButton size="small" onClick={() => setPreviewImage(url)}>
                      <ZoomInIcon fontSize="small" />
                    </IconButton>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Paper>
        )}

        {/* Preview Dialog */}
        <Dialog open={!!previewImage} onClose={() => setPreviewImage('')} maxWidth="md">
          <DialogTitle>Attachment Preview</DialogTitle>
          <DialogContent>
            {previewImage && <img src={previewImage} alt="Preview" style={{ width: '100%' }} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewImage('')}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
};

export default ViewCustomerRefundPage;
